import { getFirebase } from '@/services/firebaseApp'

import { Credential, ProviderExtras, ProviderFailure } from '@/features/Auth/providers/types'
import { pendingLink } from '@/features/Auth/session/pendingLink'

export type EstablishedSession = {
  uid: string
  extras?: ProviderExtras
}

/**
 * The one place a credential becomes a Firebase session.
 *
 * Each `kind` is redeemed differently, and putting that here is what lets a provider hand
 * back whichever shape its source produces without every caller learning all three. It is
 * also the single seam EPIC-004 has to move when the exchange goes server-side.
 *
 * On web most credentials arrive as `session`, because `signInWithPopup` signs in and
 * returns the credential in one call — see `providers/popup.ts`. `customToken` is the one
 * that still has something to redeem here, and it is the shape EPIC-004 is moving every
 * provider towards.
 */
type SignedInUser = { uid: string; email?: string | null; displayName?: string | null }

/**
 * What the pool knows about the person, merged over whatever the provider volunteered.
 *
 * The address is the load-bearing part: Google and Apple do not always hand one back, and it
 * is read off the Firebase user after signing in. Losing it leaves the signup form with an
 * empty address field that is also disabled, which cannot be submitted and cannot be
 * corrected.
 */
function detailsOf(user: SignedInUser, extras?: ProviderExtras): ProviderExtras {
  return {
    ...extras,
    email: extras?.email ?? user.email ?? undefined,
    displayName: extras?.displayName ?? user.displayName ?? undefined
  }
}

/**
 * Attaches a credential that was refused earlier for belonging to a known address.
 *
 * A failure here is deliberately swallowed: the person is signed in, which is what they
 * asked for, and a provider that could not be attached is not a reason to fail that.
 */
async function redeemPendingLink() {
  const held = pendingLink.get()

  if (!held || held.credential.kind !== 'firebase') return

  try {
    const firebase = await getFirebase()

    await firebase.auth().currentUser?.linkWithCredential(held.credential.credential)
  } catch (error) {
    logger.warn('Could not attach the held credential', error, 'Auth')
  } finally {
    pendingLink.clear()
  }
}

export async function exchange(credential: Credential): Promise<EstablishedSession> {
  const { extras } = credential

  const firebase = await getFirebase()

  try {
    if (credential.kind === 'session') {
      // The provider had to sign in to produce the credential at all — the popup, or e-mail
      // creating or recovering an account — so there is nothing left to redeem.
      await redeemPendingLink()

      return { uid: credential.uid, extras: detailsOf(firebase.auth().currentUser ?? credential, extras) }
    }

    if (credential.kind === 'customToken') {
      const result = await firebase.auth().signInWithCustomToken(credential.token)

      await redeemPendingLink()

      return { uid: result.user.uid, extras: detailsOf(result.user, extras) }
    }

    const result = await firebase.auth().signInWithCredential(credential.credential)

    await redeemPendingLink()

    return { uid: result.user.uid, extras: detailsOf(result.user, extras) }
  } catch (error) {
    if ((error as { code?: string })?.code === 'auth/account-exists-with-different-credential') {
      pendingLink.hold({ provider: credential.provider, credential })

      throw new ProviderFailure(credential.provider, 'conflict', undefined, { cause: error })
    }

    throw new ProviderFailure(credential.provider, 'rejected', 'The credential was refused', { cause: error })
  }
}
